import * as bootstrap from 'bootstrap';
import debounce from 'lodash.debounce';

class NavScroll {
    constructor(element, config) {
        const defaultConfig = {
            swipeLength: 150,
            swipeTime: 200,
            root: 'window',
        }
        this.config = Object.assign({}, defaultConfig, config);
        this.element = element;
        this.swipeState = {
            swipeMoving: false,
            swipeDirection: '',
        }
        this.swiping = false;
        this.activeDropdown = null;
        this.activeDropdownMenu = null;

        this._initDomReferences();
        this._init();
        this.reloadNav = this.reloadNav.bind(this);
        window.addEventListener('resize', debounce(this.reloadNav, 250));
    }

    _initDomReferences() {
        this.swipeNav = this.element.querySelector('.nav-scroll-container');
        this.swipeNavContent = this.swipeNav.querySelector('.nav');
        this.swipeNavItemDropdown = this.swipeNavContent.querySelectorAll('.nav-item .dropdown');
        this.swipeBtnLeft = this.element.querySelector('.nav-scroll-btn-left');
        this.swipeBtnRight = this.element.querySelector('.nav-scroll-btn-right');
    }

    _getBootstrap() {
        return bootstrap;
    }

    _getDropdownToggle(dropdown) {
        if (!dropdown) {
            return null;
        }

        return dropdown.querySelector('[data-bs-toggle="dropdown"], [data-toggle="dropdown"]');
    }

    _moveDropdownMenuToBody(dropdown) {
        const menu = dropdown ? dropdown.querySelector('.dropdown-menu') : null;
        if (!menu) {
            return null;
        }

        const dropdownRect = dropdown.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();
        const left = dropdownRect.left + window.scrollX - menuRect.width + dropdown.offsetWidth;
        const top = dropdownRect.top + window.scrollY + dropdown.offsetHeight;

        menu.style.position = 'absolute';
        menu.style.top = `${top}px`;
        menu.style.left = `${left}px`;
        menu.style.width = `${menuRect.width}px`;
        menu.style.minWidth = `${menuRect.width}px`;

        document.body.appendChild(menu);

        this.activeDropdown = dropdown;
        this.activeDropdownMenu = menu;
        return menu;
    }

    _restoreDropdownMenu(dropdown) {
        if (!dropdown) {
            return;
        }

        const menu = this.activeDropdownMenu || dropdown.querySelector('.dropdown-menu');
        if (!menu) {
            return;
        }

        dropdown.appendChild(menu);
        menu.removeAttribute('style');

        if (this.activeDropdown === dropdown) {
            this.activeDropdown = null;
            this.activeDropdownMenu = null;
        }
    }

    _init() {
        if(this.element.classList.contains('nav-scroll-init')){
            return false;
        }

        if (this.swipeNavItemDropdown.length) {
            this.swipeNavItemDropdown.forEach((dropdown) => {
                dropdown.addEventListener('show.bs.dropdown', (e) => {
                    const currentDropdown = e.currentTarget;
                    this._moveDropdownMenuToBody(currentDropdown);
                });

                dropdown.addEventListener('hide.bs.dropdown', (e) => {
                    const currentDropdown = e.currentTarget;
                    this._restoreDropdownMenu(currentDropdown);
                });
            });
        }

        if (this.swipeBtnRight) {
            this.swipeBtnRight.addEventListener('click', (e) => {
                this.swipeNavContent.querySelectorAll('li').forEach(element => {
                    const elementRect = element.getBoundingClientRect();
                    if ( elementRect.left + element.offsetWidth > this.element.width ) {
                        this.config.swipeLength = (elementRect.left + element.offsetWidth + 20) - this.element.width;
                        return false;
                    }
                })
                this._swipeTo('right', 'left', this.config.swipeLength);
            })
        }

        if (this.swipeBtnLeft) {
            this.swipeBtnLeft.addEventListener('click', (e) => {
                this.swipeNavContent.querySelectorAll('li').forEach(element => {
                    const elementRect = element.getBoundingClientRect();
                    if (elementRect.left + element.offsetWidth - 20 > 0 && elementRect.left + element.offsetWidth - 20 < element.offsetWidth) {
                        this.config.swipeLength = -(elementRect.left - 40);
                        return false;
                    }
                })
                this._swipeTo('left', 'right', -this.config.swipeLength);
            })
        }

        this.swipeNav.addEventListener('scroll', () => {
            if (!this.swiping) {
                window.requestAnimationFrame(() => {
                    this.element.setAttribute('data-overflowing', this._swipeDirection());
                    this.swiping = false;
                });
            }

            this.swiping = true;

            if (this.swipeNavItemDropdown.length && this.activeDropdownMenu && document.body.contains(this.activeDropdownMenu)) {
                if (this.activeDropdown) {
                    this._restoreDropdownMenu(this.activeDropdown);
                    const bootstrap = this._getBootstrap();
                    const toggle = this._getDropdownToggle(this.activeDropdown);
                    if (bootstrap && toggle) {
                        const dropdownInstance = bootstrap.Dropdown.getInstance(toggle) || bootstrap.Dropdown.getOrCreateInstance(toggle);
                        dropdownInstance.hide();
                    }
                }
            }
        })

        this.element.setAttribute('data-overflowing', this._swipeDirection());
        this.showActiveItem();

        this.element.classList.add('nav-scroll-init');
    }

    _swipeDirection() {
        const navSizeRect = this.swipeNav.getBoundingClientRect();
        const navSizeRight = Math.floor(navSizeRect.right);
        const navSizeLeft = Math.floor(navSizeRect.left);

        const contentSizeRect = this.swipeNavContent.getBoundingClientRect();
        const contentSizeRight = Math.floor(contentSizeRect.right) -1;
        const contentSizeLeft = Math.floor(contentSizeRect.left);

        if (navSizeLeft > contentSizeLeft && navSizeRight < contentSizeRight) {
            return 'both';
        } else if (contentSizeLeft < navSizeLeft) {
            return 'left';
        } else if (contentSizeRight > navSizeRight) {
            return 'right';
        } else {
            return 'none';
        }
    }

    _swipeTo(direction, newDirection, length) {
        if (this.swipeState.swipeMoving === true) {
            return;
        }
        if (this._swipeDirection() === direction || this._swipeDirection() === 'both') {
            this.swipeState.swipeMoving = true;

            this._animateScroll(this.swipeNav, length, this.config.swipeTime, () => {
                this.swipeState.swipeDirection = '';
                this.swipeState.swipeMoving = false;
                this.config.swipeTime = 200;
            })

            this.swipeState.swipeDirection = newDirection;
        }
    }

    _animateScroll(element, length, swipeTime, callback) {
        const currentScrollLeft = element.scrollLeft;
        const targetScrollLeft = currentScrollLeft + length;

        element.scrollTo({
            left: targetScrollLeft,
            behavior: 'smooth'
        });

        setTimeout(() => {
            if (callback && typeof callback === 'function') {
                callback.call(this);
            }
        }, swipeTime);
    }

    showActiveItem() {
        this.activeNavItem = this.swipeNavContent.querySelector('.active');
        if (this.activeNavItem && this.swipeNav) {
            const activeNavItemRect = this.activeNavItem.getBoundingClientRect();
            const swipeNavRect = this.swipeNav.getBoundingClientRect();

            const activeNavItemPos = activeNavItemRect.right - swipeNavRect.left;
            if (activeNavItemPos > this.swipeNav.offsetWidth - 50) {
                this.config.swipeTime = 0;
                this._swipeTo('right', 'left', (activeNavItemPos - this.swipeNav.offsetWidth) + 40);
            } else if (activeNavItemRect.left - swipeNavRect.left < 60) {
                this.config.swipeTime = 0;
                this._swipeTo('left', 'right', (activeNavItemPos - this.activeNavItem.offsetWidth) - 40);
            }
        }
    }

    reloadNav() {
        this.element.setAttribute('data-overflowing', this._swipeDirection());
        this.showActiveItem();
        if (this.swipeNavItemDropdown.length) {
            const bootstrap = this._getBootstrap();
            if (bootstrap) {
                this.element.querySelectorAll('[data-bs-toggle="dropdown"], [data-toggle="dropdown"]').forEach((toggle) => {
                    const dropdownInstance = bootstrap.Dropdown.getInstance(toggle) || bootstrap.Dropdown.getOrCreateInstance(toggle);
                    dropdownInstance.hide();
                });
            }
        }
    }
}

const navScroll = function(elements, config) {
    if(elements instanceof NodeList) {
        elements.forEach(element => {
            return new NavScroll(element, config);
        })
    } else {
        return new NavScroll(elements, config)
    }
}

export default navScroll;
